import {
  ChevronDown,
  Home,
  Inbox,
  LayoutGrid,
  LogOut,
  Pin,
  PinOff,
  Settings,
  User,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { signOut, useNotificationStore, useUserStore } from "@slumbr/core";
import type { PresenceEffectiveStatus, PresenceVisibility, SlumbrUser } from "@slumbr/core";
import { cn } from "@slumbr/ui";
import { usePresenceStore } from "@/stores/presence-store";
import { useOpenAppsStore } from "@/stores/open-apps-store";
import { usePinnedAppsStore } from "@/stores/pinned-apps-store";
import { findAppById } from "./app-registry";
import { ProfileSwitcherMenu } from "@/shell/components/profile-switcher-menu";
import { unregisterPushSubscription } from "@/utils/web-notifications";

const PRESENCE_OPTIONS: Array<{ value: PresenceVisibility; label: string }> = [
  { value: "online", label: "Online" },
  { value: "away", label: "Away" },
  { value: "busy", label: "Busy" },
  { value: "offline", label: "Appear Offline" },
];

function presenceColor(status?: PresenceEffectiveStatus) {
  if (status === "online") return "bg-emerald-500";
  if (status === "away") return "bg-amber-400";
  if (status === "busy") return "bg-[#ff477e]";
  return "bg-[#536471]";
}

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAppDrawer, setShowAppDrawer] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [presenceMenuOpen, setPresenceMenuOpen] = useState(false);
  const user = useUserStore((s) => s.user);
  const logout = useUserStore((s) => s.logout);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const inboxOpen = useNotificationStore((s) => s.isOpen);
  const toggleNotifications = useNotificationStore((s) => s.toggleOpen);
  const setInboxOpen = useNotificationStore((s) => s.setOpen);
  const isOnFeed = location.pathname.startsWith("/feed");
  const selfStatus = usePresenceStore((s) => s.selfStatus);
  const presenceVisibility = usePresenceStore((s) => s.visibility);
  const setPresenceVisibility = usePresenceStore((s) => s.setVisibility);
  const openApps = useOpenAppsStore((s) => s.openApps);
  const switchApp = useOpenAppsStore((s) => s.switchApp);
  const pinned = usePinnedAppsStore((s) => s.pinned);
  const pinApp = usePinnedAppsStore((s) => s.pin);
  const unpinApp = usePinnedAppsStore((s) => s.unpin);
  const profileHref = user?.id ? `/profile/${user.id}` : "/feed";
  const selectedPresence =
    PRESENCE_OPTIONS.find((option) => option.value === presenceVisibility) ??
    PRESENCE_OPTIONS[0];
  const initials = (user?.displayName || user?.email || "U")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const orderedOpenApps = useMemo(() => {
    return Array.from(openApps.values()).sort((a, b) => a.openedAt - b.openedAt);
  }, [openApps]);

  const openAppIds = new Set(orderedOpenApps.map((a) => a.appInfo.id));
  const pinnedNotOpen = pinned
    .filter((id) => !openAppIds.has(id))
    .map((id) => findAppById(id))
    .filter((app): app is NonNullable<typeof app> => Boolean(app));

  async function handleLogout() {
    // Best-effort: remove this device's push subscription while we still
    // hold auth tokens (signOut clears them) so a signed-out browser stops
    // receiving pushes for this account.
    await unregisterPushSubscription().catch(() => {});
    await signOut();
    logout();
    setShowAccountMenu(false);
    navigate("/login");
  }

  function go(path: string) {
    setShowAppDrawer(false);
    setShowAccountMenu(false);
    setInboxOpen(false);
    navigate(path);
  }

  function handleOpenApp(appId: string, route: string) {
    switchApp(appId);
    setShowAppDrawer(false);
    navigate(route);
  }

  return (
    <>
      {showAppDrawer && (
        <AppDrawer
          user={user}
          initials={initials}
          selfStatus={selfStatus?.effectiveStatus}
          openApps={orderedOpenApps}
          pinnedNotOpen={pinnedNotOpen}
          pinned={pinned}
          onPinApp={pinApp}
          onUnpinApp={unpinApp}
          onClose={() => setShowAppDrawer(false)}
          onOpenApp={handleOpenApp}
          onAllApps={() => go("/home")}
          onFeed={() => go("/feed")}
          onOpenAccount={() => {
            setShowAppDrawer(false);
            setShowAccountMenu(true);
          }}
        />
      )}

      {showAccountMenu && (
        <AccountMenu
          user={user}
          initials={initials}
          profileHref={profileHref}
          selectedPresence={selectedPresence}
          presenceVisibility={presenceVisibility}
          setPresenceVisibility={setPresenceVisibility}
          presenceMenuOpen={presenceMenuOpen}
          setPresenceMenuOpen={setPresenceMenuOpen}
          selfStatus={selfStatus?.effectiveStatus}
          onClose={() => setShowAccountMenu(false)}
          onLogout={() => void handleLogout()}
          go={go}
        />
      )}

      <nav
        className="relative z-[70] flex h-14 shrink-0 items-center justify-around border-t border-[#191c20] bg-[#0b0d0f]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <TabButton
          icon={<Home className="h-6 w-6" strokeWidth={2.2} />}
          label="Home"
          active={isOnFeed && !showAppDrawer && !inboxOpen}
          onClick={() => {
            setShowAppDrawer(false);
            setShowAccountMenu(false);
            setInboxOpen(false);
            navigate("/feed");
          }}
        />
        <TabButton
          icon={<LayoutGrid className="h-6 w-6" strokeWidth={2.2} />}
          label="Apps"
          active={showAppDrawer}
          onClick={() => {
            setInboxOpen(false);
            setShowAccountMenu(false);
            setShowAppDrawer((value) => !value);
          }}
        />
        <TabButton
          icon={<Inbox className="h-6 w-6" strokeWidth={2.2} />}
          label="Inbox"
          active={inboxOpen}
          badge={unreadCount}
          onClick={() => {
            setShowAppDrawer(false);
            setShowAccountMenu(false);
            toggleNotifications();
          }}
        />
      </nav>
    </>
  );
}

interface AppEntry {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  route: string;
}

function TabButton({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "relative flex h-12 w-16 flex-col items-center justify-center gap-0.5 transition active:scale-95",
        active ? "text-[#ff6b8f]" : "text-[#71767b] hover:text-[#d7dbdc]"
      )}
    >
      {icon}
      <span className="text-[10px] font-bold leading-none">{label}</span>
      {badge && badge > 0 ? (
        <span className="absolute right-2 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#ff477e] px-1 text-[10px] font-black text-white shadow-md">
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </button>
  );
}

function AppDrawer({
  user,
  initials,
  selfStatus,
  openApps,
  pinnedNotOpen,
  pinned,
  onPinApp,
  onUnpinApp,
  onClose,
  onOpenApp,
  onAllApps,
  onFeed,
  onOpenAccount,
}: {
  user: SlumbrUser | null;
  initials: string;
  selfStatus?: PresenceEffectiveStatus;
  openApps: Array<{ appInfo: AppEntry; lastRoute: string; openedAt: number }>;
  pinnedNotOpen: AppEntry[];
  pinned: string[];
  onPinApp: (id: string) => void;
  onUnpinApp: (id: string) => void;
  onClose: () => void;
  onOpenApp: (id: string, route: string) => void;
  onAllApps: () => void;
  onFeed: () => void;
  onOpenAccount: () => void;
}) {
  const [contextMenu, setContextMenu] = useState<{ appId: string; x: number; y: number } | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const heldRef = useRef(false);

  function startHold(appId: string, event: React.PointerEvent) {
    heldRef.current = false;
    const x = event.clientX;
    const y = event.clientY;
    holdTimerRef.current = window.setTimeout(() => {
      heldRef.current = true;
      setContextMenu({ appId, x, y });
      if ("vibrate" in navigator) navigator.vibrate?.(20);
    }, 500);
  }
  function endHold() {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }

  // Build the phone-grid tile list: All Apps + Feed + open apps + pinned-not-open
  const openTiles: Array<{ kind: "open"; entry: AppEntry; route: string }> = openApps.map((open) => ({
    kind: "open",
    entry: open.appInfo,
    route: open.lastRoute,
  }));
  const pinnedTiles: Array<{ kind: "pinned"; entry: AppEntry; route: string }> = pinnedNotOpen.map((entry) => ({
    kind: "pinned",
    entry,
    route: entry.route,
  }));
  const appTiles = [...openTiles, ...pinnedTiles];

  return (
    <div className="fixed inset-x-0 top-0 z-[55] bottom-14 flex flex-col bg-[#0b0d0f] text-[#e7e9ea]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <header
        className="flex shrink-0 items-center justify-between border-b border-[#191c20] bg-gradient-to-b from-[#16181c] to-[#0b0d0f] px-5 py-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <button
          type="button"
          onClick={onFeed}
          className="text-2xl font-black tracking-tight text-[#c83050]"
        >
          slumbr
        </button>
        <button
          type="button"
          onClick={onOpenAccount}
          className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-sm font-bold text-white shadow-lg ring-2 ring-[#ff6b8f]/30"
          aria-label="Open account menu"
        >
          {user?.avatar ? (
            <img src={user.avatar} alt={user.displayName} className="h-full w-full object-cover" />
          ) : (
            initials
          )}
          <span
            className={cn(
              "absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#0b0d0f]",
              presenceColor(selfStatus)
            )}
          />
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <div className="grid grid-cols-4 gap-y-6 gap-x-3">
          <AppTile
            label="Apps"
            onClick={onAllApps}
            iconElement={
              <span className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-gradient-to-br from-[#1a1d22] via-[#16181c] to-[#0b0d0f] text-[#ff6b8f] shadow-lg shadow-black/40 ring-1 ring-white/10">
                <LayoutGrid className="h-7 w-7" />
              </span>
            }
          />

          {appTiles.map(({ entry, route }) => {
            const Icon = entry.icon;
            return (
              <div
                key={entry.id}
                onPointerDown={(event) => startHold(entry.id, event)}
                onPointerUp={endHold}
                onPointerCancel={endHold}
                onPointerLeave={endHold}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setContextMenu({ appId: entry.id, x: event.clientX, y: event.clientY });
                }}
              >
                <AppTile
                  label={entry.name}
                  onClick={() => {
                    if (heldRef.current) return;
                    onOpenApp(entry.id, route);
                  }}
                  iconElement={
                    <span
                      className={cn(
                        "flex h-14 w-14 items-center justify-center rounded-[18px] text-white shadow-lg shadow-black/40",
                        entry.color
                      )}
                    >
                      <Icon className="h-7 w-7" />
                    </span>
                  }
                />
              </div>
            );
          })}
        </div>

        {appTiles.length === 0 && (
          <p className="mt-10 text-center text-sm text-[#71767b]">
            No pinned apps yet — tap{" "}
            <span className="font-bold text-[#ff6b8f]">Apps</span> to browse.
          </p>
        )}
      </main>

      {contextMenu && (
        <AppContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          isPinned={pinned.includes(contextMenu.appId)}
          onPin={() => {
            onPinApp(contextMenu.appId);
            setContextMenu(null);
          }}
          onUnpin={() => {
            onUnpinApp(contextMenu.appId);
            setContextMenu(null);
          }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Tap outside drawer body (header touches close button conceptually) */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close drawer"
        className="sr-only"
      >
        Close
      </button>
    </div>
  );
}

function AppTile({
  label,
  onClick,
  iconElement,
}: {
  label: string;
  onClick: () => void;
  iconElement: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-2xl px-1 py-1.5 transition active:scale-95"
    >
      {iconElement}
      <span className="max-w-full truncate text-xs font-semibold text-[#d7dbdc]">
        {label}
      </span>
    </button>
  );
}

function AppContextMenu({
  x,
  y,
  isPinned,
  onPin,
  onUnpin,
  onClose,
}: {
  x: number;
  y: number;
  isPinned: boolean;
  onPin: () => void;
  onUnpin: () => void;
  onClose: () => void;
}) {
  const width = 220;
  const height = 56;
  const left = Math.min(Math.max(8, x - width / 2), (typeof window !== "undefined" ? window.innerWidth : 360) - width - 8);
  const top = Math.min(y + 8, (typeof window !== "undefined" ? window.innerHeight : 640) - height - 8);
  return (
    <div className="fixed inset-0 z-[80]" onClick={onClose}>
      <div
        className="absolute overflow-hidden rounded-2xl border border-white/[0.08] bg-[#101214] py-1 shadow-2xl shadow-black/60"
        style={{ left, top, width }}
        onClick={(event) => event.stopPropagation()}
      >
        {isPinned ? (
          <button
            type="button"
            onClick={onUnpin}
            className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm font-semibold text-[#d7dbdc] transition hover:bg-white/[0.06]"
          >
            <PinOff className="h-4 w-4 text-[#ff6b8f]" />
            Unpin from drawer
          </button>
        ) : (
          <button
            type="button"
            onClick={onPin}
            className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm font-semibold text-[#d7dbdc] transition hover:bg-white/[0.06]"
          >
            <Pin className="h-4 w-4 text-[#ff6b8f]" />
            Pin to drawer
          </button>
        )}
      </div>
    </div>
  );
}

function AccountMenu({
  profileHref,
  selectedPresence,
  presenceVisibility,
  setPresenceVisibility,
  presenceMenuOpen,
  setPresenceMenuOpen,
  onClose,
  onLogout,
  go,
}: {
  user: SlumbrUser | null;
  initials: string;
  profileHref: string;
  selectedPresence: { value: PresenceVisibility; label: string };
  presenceVisibility: PresenceVisibility;
  setPresenceVisibility: (visibility: PresenceVisibility) => Promise<void> | void;
  presenceMenuOpen: boolean;
  setPresenceMenuOpen: (open: boolean | ((value: boolean) => boolean)) => void;
  selfStatus?: PresenceEffectiveStatus;
  onClose: () => void;
  onLogout: () => void;
  go: (path: string) => void;
}) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-x-0 top-0 z-[60] bottom-14 flex flex-col bg-[#0b0d0f] text-[#e7e9ea]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <header
        className="flex h-16 shrink-0 items-center justify-between border-b border-white/[0.06] px-5"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <h2 className="text-lg font-black tracking-tight text-[#e7e9ea]">Account</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full text-[#71767b] transition hover:bg-white/5"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="overflow-hidden rounded-3xl border border-white/[0.06] bg-[#111417]/60 backdrop-blur-xl">
          <ProfileSwitcherMenu onClose={onClose} />

          <div className="px-2">
            <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
          </div>

          <div className="relative px-2 py-2">
            <button
              type="button"
              onClick={() => setPresenceMenuOpen((value) => !value)}
              className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-white/[0.04]"
            >
              <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04]">
                <span
                  className={cn(
                    "h-2 w-2 rounded-full shadow-[0_0_10px_currentColor]",
                    presenceColor(presenceVisibility === "offline" ? "offline" : presenceVisibility)
                  )}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">Visibility</span>
                <span className="block truncate text-sm font-bold text-[#e7e9ea]">{selectedPresence.label}</span>
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-[#71767b] transition",
                  presenceMenuOpen && "rotate-180 text-[#ff6b8f]"
                )}
              />
            </button>
            {presenceMenuOpen && (
              <div className="mt-1.5 space-y-0.5">
                {PRESENCE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setPresenceMenuOpen(false);
                      void setPresenceVisibility(option.value);
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-bold transition hover:bg-white/[0.05]",
                      option.value === presenceVisibility
                        ? "bg-[#ff477e]/[0.08] text-[#ff8fac]"
                        : "text-[#d7dbdc]"
                    )}
                  >
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        presenceColor(option.value === "offline" ? "offline" : option.value)
                      )}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="px-2">
            <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
          </div>

          <div className="space-y-0.5 p-2">
            <Link
              to={profileHref}
              onClick={onClose}
              className="group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-bold text-[#d7dbdc] transition hover:bg-white/[0.04] hover:text-[#e7e9ea]"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04] text-[#ff6b8f] transition group-hover:bg-[#ff6b8f]/15">
                <User className="h-4 w-4" />
              </span>
              View profile
            </Link>
            <Link
              to="/settings"
              onClick={() => {
                onClose();
                go("/settings");
              }}
              className="group flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-bold text-[#d7dbdc] transition hover:bg-white/[0.04] hover:text-[#e7e9ea]"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04] text-[#71767b] transition group-hover:text-[#e7e9ea]">
                <Settings className="h-4 w-4" />
              </span>
              Settings
            </Link>
          </div>

          <div className="border-t border-white/[0.06] p-2">
            <button
              onClick={onLogout}
              className="group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-bold text-red-300 transition hover:bg-red-500/10 hover:text-red-200"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500/10 transition group-hover:bg-red-500/15">
                <LogOut className="h-4 w-4" />
              </span>
              Log out
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
